import LegalLayout from './LegalLayout';
import { RefundES } from './LegalSpanish';
import { useLanguage } from '../../i18n/LanguageContext';

const Refund = ({ user, setUser }) => {
  const { lang } = useLanguage();
  return (
    <LegalLayout
      user={user}
      setUser={setUser}
      titleKey="footer.linkRefund"
      titleFallback="Refund Policy"
      esTitle="Política de Reembolso"
      subtitleFallback="Clear, consistent rules — no fine-print surprises."
      esSubtitle="Reglas claras y consistentes — sin sorpresas en la letra pequeña."
      lastUpdated="February 17, 2026"
      testId="refund-policy-page"
    >
      {lang === 'es' ? <RefundES /> : (
        <>
    <p>
      We want you to feel confident every time you book on Match My Cleaner. This Refund Policy explains exactly when and
      how you get your money back. It applies to all bookings paid through the platform via Stripe.
    </p>

    <h2>1. Cancellation by the customer</h2>
    <p>Refunds depend on how far in advance you cancel before the scheduled service start time:</p>
    <ul>
      <li>
        <strong>24 hours or more before service</strong> — <span className="text-emerald-700 font-semibold">Full refund, no questions asked.</span>
        Cancel any time from your Customer Dashboard.
      </li>
      <li>
        <strong>Less than 24 hours before service</strong> — A late-cancellation fee applies. The current fee is
        <strong> 50% of the booking total</strong>. The remaining 50% is refunded to your original payment method.
      </li>
    </ul>
    <p>
      The late-cancellation fee is split between your cleaner and the platform (currently 50/50). This compensates your
      cleaner for the time they reserved for you and could no longer fill.
    </p>

    <h2>2. Cleaner no-show or arrives unable to perform service</h2>
    <ul>
      <li><strong>Full refund</strong> to your original payment method.</li>
      <li>We'll help you rebook with another cleaner — and apply a $10 credit toward the new booking as our apology.</li>
      <li>Report no-shows via Contact Us within 48 hours so we can act on the report against the cleaner.</li>
    </ul>

    <h2>3. Service quality issues</h2>
    <p>If the cleaning falls short of what was promised:</p>
    <ul>
      <li>Contact us within <strong>24 hours</strong> of service completion with details and (ideally) photos.</li>
      <li>We'll review with the cleaner. Common resolutions include a partial refund (typically 20–50%), a free re-clean by the same or another cleaner, or a full refund for severe issues.</li>
      <li>If the cleaner repeatedly receives quality complaints, they may lose their Premium/Elite tier and ultimately be removed from the platform.</li>
    </ul>

    <h2>4. Damage or loss during service</h2>
    <ul>
      <li>Document the damage with photos and a written description immediately.</li>
      <li>Contact us within <strong>72 hours</strong> of the service.</li>
      <li>Most cleaners on the platform carry their own liability insurance — we'll help coordinate a claim.</li>
      <li>For damages not covered by insurance, we evaluate case-by-case and may issue a goodwill credit or refund at our discretion.</li>
    </ul>

    <h2>5. Tips</h2>
    <p>
      Tips paid through the platform after a completed booking are <strong>non-refundable</strong>, since they go
      directly to your cleaner with no platform fee. If you tipped by mistake or believe the tip was processed in error,
      contact us within 48 hours and we'll review case-by-case.
    </p>

    <h2>6. Subscription / recurring bookings</h2>
    <ul>
      <li>You can cancel any future installment of a recurring booking at no charge if you cancel ≥ 24 hours before that instance.</li>
      <li>Late cancellations on any single instance follow the same 50% fee rule.</li>
      <li>Cancelling the recurring series only affects future instances — completed cleanings are not refunded.</li>
    </ul>

    <h2>7. Disputed payments / chargebacks</h2>
    <p>
      Please reach out to us first before initiating a chargeback with your bank. Most issues are resolved within 48
      hours through our refund process. Chargebacks bypass our dispute resolution and may result in your account being
      temporarily suspended pending investigation.
    </p>

    <h2>8. How refunds are processed</h2>
    <ul>
      <li>Refunds are issued to your <strong>original payment method</strong> via Stripe.</li>
      <li>Most refunds appear on your statement within <strong>5–10 business days</strong>, depending on your card issuer.</li>
      <li>You'll get an in-app notification and email confirmation when the refund is initiated.</li>
    </ul>

    <h2>9. What's not refundable</h2>
    <ul>
      <li>Services where you were not present and did not provide alternate access (counts as no-show by you).</li>
      <li>Complaints submitted more than 7 days after service.</li>
      <li>Subjective preferences not specified at booking time (e.g., "I wanted them to also clean the garage" when garage wasn't in the original scope).</li>
    </ul>

    <h2>10. Questions or appeals</h2>
    <p>
      If you disagree with a refund decision, you can appeal by replying to the resolution email with additional context.
      Appeals are reviewed by a separate trust-team member within 5 business days.
    </p>
        </>
      )}
    </LegalLayout>
  );
};

export default Refund;
